import { useCallback, useRef, useState, type ReactNode } from 'react';

import { useSiteContext } from '@/providers';
import { useLayoutStorage } from '@/stores/layout.ts';

import { useResolveShellWidgetId } from '../useResolveShellWidgetId.ts';
import { resolveWidgetDefaultSiteKey } from '../useWidgetSiteTabbedShellSave.ts';
import { WidgetSitePanelSaveHandler } from './widgetConfigSiteTabbedShell.types.ts';
import { WidgetSiteTabbedShellContext } from './widgetSiteTabbedShellProvider/WidgetSiteTabbedShellContext.ts';

type WidgetSiteTabbedShellProviderProps = {
  /** Layout grid widget id when editing; omitted when adding a new widget. */
  widgetId?: string;
  /** Override first tab key; otherwise derived from the layout widget's stored site. */
  defaultSiteKey?: string;
  children: ReactNode;
};

export const WidgetSiteTabbedShellProvider = ({
  widgetId,
  defaultSiteKey: defaultSiteKeyOverride,
  children,
}: WidgetSiteTabbedShellProviderProps) => {
  const { sites } = useSiteContext();
  const shellWidgetId = useResolveShellWidgetId(widgetId);
  const widgetSiteId = useLayoutStorage(s => (widgetId ? s.getWidgetSiteId(widgetId) : undefined));
  const resolvedDefaultSiteKey =
    defaultSiteKeyOverride ?? resolveWidgetDefaultSiteKey(widgetSiteId, sites);
  const [activeSiteKey, setActiveSiteKey] = useState(resolvedDefaultSiteKey);
  const saveHandlersRef = useRef(new Map<string, WidgetSitePanelSaveHandler>());

  const registerSave = useCallback((siteKey: string, save: WidgetSitePanelSaveHandler) => {
    saveHandlersRef.current.set(siteKey, save);
    return () => {
      if (saveHandlersRef.current.get(siteKey) === save) {
        saveHandlersRef.current.delete(siteKey);
      }
    };
  }, []);

  const saveActivePanel = useCallback(() => {
    const siteKey = activeSiteKey ?? resolvedDefaultSiteKey;
    if (!siteKey) return;
    void saveHandlersRef.current.get(siteKey)?.();
  }, [activeSiteKey, resolvedDefaultSiteKey]);

  return (
    <WidgetSiteTabbedShellContext.Provider
      value={{
        defaultSiteKey: resolvedDefaultSiteKey,
        activeSiteKey,
        setActiveSiteKey,
        registerSave,
        saveActivePanel,
        shellWidgetId,
      }}
    >
      {children}
    </WidgetSiteTabbedShellContext.Provider>
  );
};
